// Attendance Panel Component
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, UserCheck, UserX, UserMinus, Shield, 
  Loader2, MoreVertical, ClipboardList 
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const AttendancePanel = ({ 
  open, 
  onClose, 
  meetingId, 
  isChair, 
  onUpdate,
  endpointBase = '/board',
  memberLabel = 'Board Members',
  allowRollCall = true,
  showQuorum = true,
  title = 'Attendance & Quorum'
}) => {
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  
  const fetchAttendance = useCallback(async () => {
    try {
      const response = await api.get(`${endpointBase}/meetings/${meetingId}/attendance`);
      setAttendance(response.data);
    } catch (err) {
      console.error('Failed to fetch attendance:', err);
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [meetingId]);
  
  useEffect(() => {
    if (open) {
      fetchAttendance();
    }
  }, [open, fetchAttendance]);
  
  const updatePresence = async (userId, status) => {
    setUpdating(prev => ({ ...prev, [userId]: true }));
    try {
      await api.put(`${endpointBase}/meetings/${meetingId}/attendance/${userId}`, {
        presence_status: status
      });
      fetchAttendance();
      if (onUpdate) onUpdate();
      toast.success('Attendance updated');
    } catch (err) {
      toast.error('Failed to update attendance');
    } finally {
      setUpdating(prev => ({ ...prev, [userId]: false }));
    }
  };
  
  const takeRollCall = async () => {
    try {
      await api.post(`${endpointBase}/meetings/${meetingId}/attendance/roll-call`);
      fetchAttendance();
      toast.success('Roll call recorded');
    } catch (err) {
      toast.error('Failed to record roll call');
    }
  };
  
  const getPresenceIcon = (status) => {
    switch (status) {
      case 'present':
        return <UserCheck className="h-4 w-4 text-green-500" />;
      case 'absent':
        return <UserX className="h-4 w-4 text-red-500" />;
      case 'left_early':
        return <UserMinus className="h-4 w-4 text-yellow-500" />;
      case 'recused':
        return <Shield className="h-4 w-4 text-purple-500" />;
      default:
        return <Users className="h-4 w-4 text-gray-400" />;
    }
  };
  
  const getPresenceBadge = (status) => {
    switch (status) {
      case 'present':
        return <Badge className="bg-green-100 text-green-700">Present</Badge>;
      case 'absent':
        return <Badge className="bg-red-100 text-red-700">Absent</Badge>;
      case 'left_early':
        return <Badge className="bg-yellow-100 text-yellow-700">Left Early</Badge>;
      case 'recused':
        return <Badge className="bg-purple-100 text-purple-700">Recused</Badge>;
      default:
        return <Badge variant="secondary">Pending</Badge>;
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-purple-600" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
          </div>
        ) : attendance ? (
          <div className="space-y-6">
            {showQuorum && attendance.quorum && (
              <div className={`p-4 rounded-lg ${attendance.quorum.met ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">Quorum Status</h4>
                    <p className={`text-sm ${attendance.quorum.met ? 'text-green-600' : 'text-red-600'}`}>
                      {attendance.quorum.present} of {attendance.quorum.required} required members present
                    </p>
                  </div>
                  <Badge className={attendance.quorum.met ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}>
                    {attendance.quorum.met ? 'Quorum Met' : 'No Quorum'}
                  </Badge>
                </div>
              </div>
            )}
            
            {/* Actions */}
            {isChair && allowRollCall && (
              <div className="flex gap-2">
                <Button onClick={takeRollCall} variant="outline">
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Record Roll Call
                </Button>
              </div>
            )}
            
            {/* Attendee List */}
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">{memberLabel}</h4>
              {attendance.attendees.map((attendee) => (
                <div 
                  key={attendee.user_id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {getPresenceIcon(attendee.presence_status)}
                    <div>
                      <p className="font-medium text-gray-900">{attendee.name}</p>
                      <p className="text-xs text-gray-500 capitalize">{attendee.role || attendee.board_role || 'Member'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {getPresenceBadge(attendee.presence_status)}
                    
                    {isChair && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" disabled={updating[attendee.user_id]}>
                            {updating[attendee.user_id] ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <MoreVertical className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => updatePresence(attendee.user_id, 'present')}>
                            <UserCheck className="h-4 w-4 mr-2 text-green-500" />
                            Mark Present
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updatePresence(attendee.user_id, 'absent')}>
                            <UserX className="h-4 w-4 mr-2 text-red-500" />
                            Mark Absent
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updatePresence(attendee.user_id, 'left_early')}>
                            <UserMinus className="h-4 w-4 mr-2 text-yellow-500" />
                            Left Early
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updatePresence(attendee.user_id, 'recused')}>
                            <Shield className="h-4 w-4 mr-2 text-purple-500" />
                            Recused
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No attendance data available</p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AttendancePanel;
